import { Link } from "react-router-dom";
import { ArrowRight, FileSpreadsheet, Tablet, CalendarClock, ShieldCheck, Server, History, Download, Repeat } from "lucide-react";
import { INDUSTRIES } from "@/data/industries";
import { PROJECT_TYPES } from "@/data/site";

export const SectionHead = ({ eyebrow, title, text }) => (
  <div className="max-w-3xl mb-12">
    <p className="eyebrow mb-3">{eyebrow}</p>
    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-100">{title}</h2>
    {text && <p className="mt-4 text-base md:text-lg text-slate-400">{text}</p>}
  </div>
);

const Wrap = ({ id, children, className = "" }) => (
  <section id={id} data-testid={`section-${id}`} className={`py-20 md:py-28 border-t border-[#1E2D4A] ${className}`}>
    <div className="max-w-7xl mx-auto px-5 lg:px-8">{children}</div>
  </section>
);

export function Problem() {
  const items = [
    ["Spreadsheets and judgement", "Handover dates are tracked in spreadsheets and weekly meetings. The date on the programme is a hope, not a calculation."],
    ["The wrong size of tool", "Completions systems are either too heavy for the job, or disconnected from the programme the project is managed against."],
    ["The cost of a late week", "A week of late start-up or energisation costs more than the software ever will."],
  ];
  return (
    <Wrap id="problem">
      <SectionHead eyebrow="The problem" title="Most projects don't know their real handover date until it's too late to change it." />
      <div className="grid md:grid-cols-3 gap-px bg-[#1E2D4A] border border-[#1E2D4A]">
        {items.map(([t, d], i) => (
          <div key={t} className="bg-[#080D1A] p-8">
            <p className="num text-amber-500 text-sm mb-6">0{i + 1}</p>
            <h3 className="text-xl font-semibold text-slate-100 mb-3">{t}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </Wrap>
  );
}

export function HowItWorks() {
  const steps = [
    [FileSpreadsheet, "Import your tag register", "Import your tag register or equipment list. The system and subsystem breakdown is generated, with checksheets assigned from each equipment type."],
    [Tablet, "Complete ITRs in the field", "Complete ITRs and checksheets on a tablet or phone, offline, with photo evidence and signatures."],
    [CalendarClock, "See your forecast handover date", "Watch the forecast handover date update from real progress, linked to your Primavera P6 programme."],
  ];
  return (
    <Wrap id="how" className="grid-bg">
      <SectionHead eyebrow="How it works" title="Three steps from tag register to a handover date you can defend." />
      <ol className="grid lg:grid-cols-3 gap-6">
        {steps.map(([Icon, t, d], i) => (
          <li key={t} className="panel p-8 relative" data-testid={`how-step-${i + 1}`}>
            <span className="absolute top-6 right-6 num text-5xl font-bold text-[#1E2D4A]">{i + 1}</span>
            <Icon className="w-7 h-7 text-amber-500 mb-8" />
            <h3 className="text-xl font-semibold text-slate-100 mb-3">{t}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{d}</p>
          </li>
        ))}
      </ol>
    </Wrap>
  );
}

export function IndustriesGrid() {
  return (
    <Wrap id="industries">
      <SectionHead eyebrow="Industries" title="Built for any project with a commissioning phase." text="Each sector uses its own milestone language. Pulse uses yours." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#1E2D4A] border border-[#1E2D4A]">
        {INDUSTRIES.map((i) => (
          <Link key={i.slug} to={`/industries/${i.slug}`} data-testid={`industry-card-${i.slug}`}
            className="group bg-[#080D1A] hover:bg-[#0F172A] p-6 min-h-[170px] flex flex-col justify-between transition-colors">
            <span className="font-mono text-xs text-slate-500 group-hover:text-amber-500 transition-colors">{i.code}</span>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 leading-tight">{i.name}</h3>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">{i.milestone} <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></p>
            </div>
          </Link>
        ))}
      </div>
    </Wrap>
  );
}

export function ProjectTypes() {
  return (
    <Wrap id="project-types">
      <SectionHead eyebrow="Project types" title="From megaprojects to a two-week outage." />
      <div className="divide-y divide-[#1E2D4A] border-y border-[#1E2D4A]">
        {PROJECT_TYPES.map((p, i) => (
          <div key={p.name} className="grid md:grid-cols-12 gap-4 py-6 group">
            <span className="md:col-span-1 num text-slate-500 group-hover:text-amber-500 transition-colors">0{i + 1}</span>
            <h3 className="md:col-span-4 text-xl font-semibold text-slate-100">{p.name}</h3>
            <p className="md:col-span-7 text-slate-400">{p.text}</p>
          </div>
        ))}
      </div>
    </Wrap>
  );
}

export function Integrations() {
  return (
    <Wrap id="integrations">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <SectionHead eyebrow="Works alongside your existing systems" title="Report into the client's system without double entry."
          text="Import from and export to the formats used by major completions systems and client CMS. Subcontractors can run their work in Pulse and report into a client's mandated system." />
        <div className="panel p-6 font-mono text-xs space-y-3" data-testid="integrations-panel">
          {[["Tag register", "CSV / XLSX", "import + export"], ["Checksheet status", "CSV / XLSX", "import + export"], ["Punch list", "CSV / XLSX", "import + export"], ["Primavera P6 programme", "XER", "import"], ["Handover dossier", "PDF", "export"]].map(([a, b, c]) => (
            <div key={a} className="flex items-center justify-between border-b border-[#1E2D4A] pb-3 last:border-0 last:pb-0">
              <span className="text-slate-200 flex items-center gap-2"><Repeat className="w-3.5 h-3.5 text-amber-500" />{a}</span>
              <span className="text-slate-500">{b} · {c}</span>
            </div>
          ))}
        </div>
      </div>
    </Wrap>
  );
}

export function Security() {
  const items = [[Server, "UK hosting", "Your data is hosted in the UK."], [ShieldCheck, "Role-based access", "Admin, Manager, Field user and Viewer roles. Clients, lenders and auditors get read-only access."],
    [History, "Full audit trail", "Every change is recorded: who, what and when."], [Download, "Export at any time", "Your data, in standard formats, whenever you want it."]];
  return (
    <Wrap id="security">
      <SectionHead eyebrow="Security" title="Your records, under your control." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map(([Icon, t, d]) => (
          <div key={t} className="border-l-2 border-amber-500 pl-5">
            <Icon className="w-5 h-5 text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-100 mb-2">{t}</h3>
            <p className="text-sm text-slate-400">{d}</p>
          </div>
        ))}
      </div>
    </Wrap>
  );
}
